// ComediatesListPage.jsx con mensaje 404 mejorado
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaVideo, FaUser, FaTimes } from 'react-icons/fa';
import { getComediantesPaginados } from '../../http/Actions/ComedianteAction';
import Pagination from '../../Pagination';
import sinFoto from '../../../images/sin-foto.png';

const ComediantesListPage = () => {
  // Estados para manejar los datos y la UI
  const [comediantes, setComediantes] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado específico para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8); // Fijo para simplificar

  // Manejador de respuesta para extraer correctamente los datos
  const handleComediantesResponse = (response) => {
    // Verifica si la respuesta tiene la estructura esperada
    if (response && response.data) {
      if (response.status === 404) {
        // Si es un 404, establecer un array vacío para los datos
        setComediantes({
          data: [],
          totalPages: 0,
          currentPage: 1,
          hasNext: false,
          hasPrevious: false,
          totalCount: 0
        });
        // Limpiar el error previo si existía
        setError(null);
      } else if (response.data && response.data.data) {
        // Si los datos están anidados, extraerlos correctamente
        setComediantes(response.data);
        // Limpiar el error previo si existía
        setError(null);
      } else {
        // Si los datos están directamente en response.data
        setComediantes(response);
        // Limpiar el error previo si existía
        setError(null);
      }
    } else {
      console.error("Estructura de respuesta inesperada:", response);
      setError("Formato de datos inesperado");
    }
  };

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Al cambiar la búsqueda, volvemos a la página 1
      setCurrentPage(1);
      // Limpiar los errores anteriores cuando cambia la búsqueda
      setError(null);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para cargar los comediantes
  const fetchComediantes = async (page, size, search) => {
    setIsLoading(true);
    // Limpiar cualquier error previo al iniciar una nueva búsqueda
    setError(null);
    const token = localStorage.getItem('token');
    try {
      await getComediantesPaginados(
        handleComediantesResponse, // Usa el manejador personalizado
        setError,
        page,
        size,
        search, 
        token
      );
    } catch (error) {
      console.error("Error fetching comedians:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar los comediantes cuando cambian los parámetros
  useEffect(() => {
    fetchComediantes(currentPage, pageSize, debouncedSearchTerm);
  }, [currentPage, pageSize, debouncedSearchTerm]);

  // Función para manejar el cambio de página
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Función para limpiar la búsqueda
  const clearSearch = () => {
    setSearchTerm('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="container mx-auto px-4">
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-6 md:mb-0">
            <FaUser className="inline-block mr-3 text-red-500" />
            Comediantes
          </h1>
          
          {/* Buscador */}
          <div className="w-full md:w-auto md:min-w-[300px]">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por nombre de comediante..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg py-3 px-5 pl-12 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              
              {/* Botón para limpiar búsqueda */}
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  aria-label="Limpiar búsqueda"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Estado de carga */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-16 h-16 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Lista de comediantes */}
        {!isLoading && (
          <>
            {/* Errores técnicos (no 404) */}
            {error && (
              <div className="bg-zinc-800/50 rounded-lg p-12 text-center">
                <FaUser className="mx-auto text-4xl text-gray-500 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Ocurrió un error al cargar los comediantes</h3>
                <p className="text-gray-400">{error}</p>
              </div>
            )}

            {/* Comediantes encontrados */}
            {!error && comediantes && comediantes.data && comediantes.data.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {comediantes.data.map((comediante) => (
                  <Link 
                    to={`/comediante/${comediante.comedianteId}`} 
                    key={comediante.comedianteId}
                    className="group"
                  >
                    <div className={`bg-zinc-800 rounded-xl overflow-hidden shadow-lg transition-all duration-300 h-full flex flex-col hover:bg-zinc-700 hover:shadow-red-500/10 ${!comediante.habilitado ? 'opacity-60' : ''}`}>
                      {/* Imagen del comediante */}
                      <div className="relative">
                        <div className="aspect-[3/4] overflow-hidden">
                          <img 
                            src={
                              comediante.imagen && comediante.imagen.trim() !== ''
                                ? comediante.imagen
                                : sinFoto
                            }
                            alt={comediante.nombre} 
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = sinFoto;
                            }}
                          />
                        </div>
                        
                        {/* Indicador de estado */}
                        {!comediante.habilitado && (
                          <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                            No disponible
                          </div>
                        )}
                        
                        {/* Overlay con información */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end opacity-100">
                          <div className="p-4 w-full">
                            <h2 className="text-white text-lg font-bold truncate">{comediante.nombre}</h2>
                          </div>
                        </div>
                      </div>
                      
                      {/* Detalles */}
                      <div className="p-4 flex-grow flex flex-col justify-between">
                        <p className="text-gray-300 text-sm line-clamp-3 mb-4">
                          {comediante.biografia.length > 120
                            ? `${comediante.biografia.substring(0, 120)}...`
                            : comediante.biografia}
                        </p>
                        
                        <div className="flex justify-between items-center text-sm text-gray-400">
                          <span className="flex items-center">
                            <FaVideo className="mr-1" />
                            {comediante.cantidadShows} {comediante.cantidadShows === 1 ? 'video' : 'videos'}
                          </span>
                          <span className="bg-red-600/20 text-red-300 px-2 py-1 rounded-full text-xs">
                            Ver perfil
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              // No se encontraron comediantes (mensaje mejorado similar a tu imagen 2)
              !error && !isLoading && (
                <div className="bg-zinc-800/50 rounded-lg p-12 text-center">
                  <FaUser className="mx-auto text-4xl text-gray-500 mb-4" />
                  <h3 className="text-xl font-medium text-white mb-2">No se encontraron comediantes</h3>
                  <p className="text-gray-400">
                    {searchTerm 
                      ? `No hay resultados para "${searchTerm}". Intenta con otros términos.` 
                      : 'Aún no hay comediantes registrados en el sistema.'}
                  </p>
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="mt-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              )
            )}
            
            {/* Paginación */}
            {!error && comediantes && comediantes.data && comediantes.data.length > 0 && (
              <div className="mt-12">
                <Pagination 
                  currentPage={currentPage}
                  totalPages={comediantes.totalPages || 1}
                  hasNext={comediantes.hasNext || false}
                  hasPrevious={comediantes.hasPrevious || false}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ComediantesListPage;